import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type { CalendarEventItem, OfficeCalendarSession } from '../types/calendar';
import type { EventPayload } from '../schemas/event.schema';

/** What the list endpoint narrows on. Omitted, the whole session comes back. */
export interface CalendarListParams {
  /** `YYYY-MM` — every entry overlapping that month. */
  month?: string;
  category?: CalendarEventItem['category'];
  /** A past session to read. Omitted, the current one — and writes always go there. */
  academicYearUuid?: string;
}

/**
 * Every call this feature makes. As with the rest of the institute area there is
 * no institute id in any path — the server takes it from the caller's token, so
 * there is nothing for a client to point at another institute's calendar.
 *
 * The academic year written to is the server's decision too: an entry is
 * always planned against the institute's current session. A past one can only
 * be read, through `academicYearUuid` on the list.
 *
 * Holidays arrive in `list` but are not written here. They are rows in
 * `holidays`, which the attendance register reads, and are written through
 * `holidaysApi` — see `source` on `CalendarEventItem`.
 */
export const calendarApi = {
  list: (params: CalendarListParams = {}) =>
    unwrap(
      apiClient.get<ApiSuccess<OfficeCalendarSession>>('/institute/calendar', {
        // Empty filters are dropped rather than sent as blanks — the server
        // validates the shape of what arrives, and `category=` is not a category.
        params: {
          ...(params.month && { month: params.month }),
          ...(params.category && { category: params.category }),
          ...(params.academicYearUuid && { academicYearUuid: params.academicYearUuid }),
        },
      }),
    ),

  create: (payload: EventPayload) =>
    unwrap(apiClient.post<ApiSuccess<CalendarEventItem>>('/institute/calendar', payload)),

  update: (uuid: string, payload: EventPayload) =>
    unwrap(apiClient.patch<ApiSuccess<CalendarEventItem>>(`/institute/calendar/${uuid}`, payload)),

  remove: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/calendar/${uuid}`)),
};
